import { useEffect, useMemo, useState } from "react";

import useCampaignStore from "../../store/campaignStore";
import {
  useCreateCampaign,
  useUpdateCampaign,
} from "../../features/capmpaign/useCampaigns";

const toInputDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);
  return localDate.toISOString().slice(0, 16);
};

const toIsoDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const getInitialForm = (campaign) => ({
  title: campaign?.title || "",
  slug: campaign?.slug || "",
  description: campaign?.description || "",
  starts_at: toInputDate(campaign?.starts_at),
  ends_at: toInputDate(campaign?.ends_at),
  display_order: campaign?.display_order ?? 0,
  is_active: campaign?.is_active ?? true,
  destination: campaign?.destination || "products",
  desktop_banner: null,
  mobile_banner: null,
});

const getProductId = (product) => {
  return product?.product_id ?? product?.id ?? product?.product?.id ?? null;
};

const inputClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 outline-none transition-colors duration-200 placeholder:text-gray-400 focus:border-[#1E3A5F]";
const textareaClass =
  "w-full resize-y rounded-lg border border-gray-300 bg-white p-3 text-sm text-gray-700 outline-none transition-colors duration-200 placeholder:text-gray-400 focus:border-[#1E3A5F]";
const labelClass = "mb-1.5 block text-sm font-medium text-gray-700";
const errorTextClass = "mt-1 text-sm text-red-600";
const fileInputClass =
  "block w-full cursor-pointer rounded-lg border border-gray-300 bg-white p-2 text-sm text-gray-600 file:ml-3 file:rounded-md file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-gray-700";
const btnBase =
  "flex items-center justify-center gap-2 px-8 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed";
const btnPrimary =
  "bg-[#1E3A5F] text-[#FFC107] hover:bg-[#2B5388] hover:text-[#D19D00]";
const btnSecondary =
  "bg-[#F5F5F5] text-[#1E3A5F] hover:bg-[#DBDBDB] hover:text-[#12233A]";

const CampaignForm = ({ campaign = null, onSuccess, onCancel }) => {
  const [form, setForm] = useState(() => getInitialForm(campaign));
  const [errors, setErrors] = useState({});

  const createMutation = useCreateCampaign();
  const updateMutation = useUpdateCampaign();

  const resetCampaignDraft = useCampaignStore(
    (state) => state.resetCampaignDraft
  );
  const selectedProducts = useCampaignStore((state) => state.selectedProducts);
  const setSelectedProducts = useCampaignStore(
    (state) => state.setSelectedProducts
  );

  useEffect(() => {
    setForm(getInitialForm(campaign));
    setErrors({});
    setSelectedProducts(campaign?.products || []);
  }, [campaign, setSelectedProducts]);

  const isEditMode = Boolean(campaign?.slug);
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const updateField = (name, value) => {
    setForm((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({
      ...previous,
      [name]: undefined,
      general: undefined,
    }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.title.trim()) {
      nextErrors.title = "عنوان رویداد الزامی است.";
    }

    if (form.title.length > 200) {
      nextErrors.title =
        "عنوان رویداد نمی‌تواند بیشتر از ۲۰۰ کاراکتر باشد.";
    }

    if (!form.starts_at) {
      nextErrors.starts_at = "زمان شروع الزامی است.";
    }

    if (!form.ends_at) {
      nextErrors.ends_at = "زمان پایان الزامی است.";
    }

    if (
      form.starts_at &&
      form.ends_at &&
      new Date(form.starts_at) >= new Date(form.ends_at)
    ) {
      nextErrors.ends_at = "زمان پایان باید بعد از زمان شروع باشد.";
    }

    if (form.display_order === "" || Number(form.display_order) < 0) {
      nextErrors.display_order =
        "ترتیب نمایش باید عددی بزرگ‌تر یا مساوی صفر باشد.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => {
    const productIds = (selectedProducts || [])
      .map(getProductId)
      .filter(Boolean);

    const startsAt = toIsoDate(form.starts_at);
    const endsAt = toIsoDate(form.ends_at);

    const hasFile = Boolean(form.desktop_banner || form.mobile_banner);

    if (hasFile) {
      const formData = new FormData();

      formData.append("title", form.title.trim());
      formData.append("slug", form.slug.trim());
      formData.append("description", form.description || "");
      if (startsAt) formData.append("starts_at", startsAt);
      if (endsAt) formData.append("ends_at", endsAt);
      formData.append("display_order", String(Number(form.display_order)));
      formData.append("is_active", String(Boolean(form.is_active)));
      formData.append("destination", form.destination);

      productIds.forEach((productId) => {
        formData.append("product_ids", String(productId));
      });

      if (form.desktop_banner instanceof File) {
        formData.append("desktop_banner", form.desktop_banner);
      }

      if (form.mobile_banner instanceof File) {
        formData.append("mobile_banner", form.mobile_banner);
      }

      return formData;
    }

    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim(),
      description: form.description || "",
      starts_at: startsAt,
      ends_at: endsAt,
      display_order: Number(form.display_order),
      is_active: Boolean(form.is_active),
      destination: form.destination,
    };

    if (productIds.length > 0) {
      payload.product_ids = productIds;
    }

    return payload;
  };

  const getApiErrors = (error) => {
    const responseData = error?.response?.data;

    if (!responseData) {
      return {
        general: "ذخیره رویداد انجام نشد. اطلاعات واردشده را بررسی کنید.",
      };
    }

    if (typeof responseData === "string") {
      return { general: responseData };
    }

    if (typeof responseData === "object") {
      return responseData;
    }

    return {
      general: "ذخیره رویداد انجام نشد. اطلاعات واردشده را بررسی کنید.",
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) return;

    try {
      const payload = buildPayload();

      if (isEditMode) {
        await updateMutation.mutateAsync({
          slug: campaign.slug,
          payload,
        });
        onSuccess?.(campaign);
      } else {
        const createdCampaign = await createMutation.mutateAsync(payload);
        onSuccess?.(createdCampaign);
      }

      resetCampaignDraft();
    } catch (error) {
      setErrors(getApiErrors(error));
    }
  };

  const desktopPreview = useMemo(() => {
    if (form.desktop_banner instanceof File) {
      return URL.createObjectURL(form.desktop_banner);
    }
    return campaign?.desktop_banner || null;
  }, [form.desktop_banner, campaign?.desktop_banner]);

  const mobilePreview = useMemo(() => {
    if (form.mobile_banner instanceof File) {
      return URL.createObjectURL(form.mobile_banner);
    }
    return campaign?.mobile_banner || null;
  }, [form.mobile_banner, campaign?.mobile_banner]);

  return (
    <form onSubmit={handleSubmit} dir="rtl" className="mx-auto max-w-5xl space-y-6">
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-4 py-4 sm:px-5">
          <h2 className="text-sm font-semibold text-gray-800 sm:text-base">
            اطلاعات رویداد
          </h2>
        </div>

        <div className="p-4 sm:p-5">
          {errors.general && (
            <div className="mb-5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {typeof errors.general === "string"
                ? errors.general
                : "اطلاعات فرم معتبر نیست."}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              <label className={labelClass}>عنوان رویداد</label>
              <input
                value={form.title}
                onChange={(e) => updateField("title", e.target.value)}
                className={inputClass}
              />
              {errors.title && (
                <p className={errorTextClass}>
                  {Array.isArray(errors.title) ? errors.title.join(" ") : errors.title}
                </p>
              )}
            </div>

            <div>
              <label className={labelClass}>اسلاگ</label>
              <input
                value={form.slug}
                onChange={(e) => updateField("slug", e.target.value)}
                placeholder="اختیاری"
                className={`${inputClass} text-left`}
                  />
              {errors.slug && (
                <p className={errorTextClass}>
                  {Array.isArray(errors.slug) ? errors.slug.join(" ") : errors.slug}
                </p>
              )}
            </div>

            <div className="lg:col-span-2">
              <label className={labelClass}>توضیحات</label>
              <textarea
                rows={4}
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
                className={textareaClass}
              />
              {errors.description && (
                <p className={errorTextClass}>
                  {Array.isArray(errors.description)
                    ? errors.description.join(" ")
                    : errors.description}
                </p>
              )}
            </div>

            <div>
              <label className={labelClass}>زمان شروع</label>
              <input
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => updateField("starts_at", e.target.value)}
                className={inputClass}
              />
              {errors.starts_at && (
                <p className={errorTextClass}>
                  {Array.isArray(errors.starts_at)
                    ? errors.starts_at.join(" ")
                    : errors.starts_at}
                </p>
              )}
            </div>

            <div>
              <label className={labelClass}>زمان پایان</label>
              <input
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => updateField("ends_at", e.target.value)}
                className={inputClass}
              />
              {errors.ends_at && (
                  <p className={errorTextClass}>
                    {Array.isArray(errors.ends_at)
                        ? errors.ends_at.join(" ")
                        : errors.ends_at}
                  </p>
              )}
            </div>

            <div>
              <label className={labelClass}>ترتیب نمایش</label>

              <input
                type="number"
                min="0"
                value={form.display_order}
                onChange={(e) => updateField("display_order", e.target.value)}
                className={inputClass}
              />
              {errors.display_order && (
                <p className={errorTextClass}>
                  {Array.isArray(errors.display_order)
                    ? errors.display_order.join(" ")
                    : errors.display_order}
                </p>
              )}
            </div>

            <div>
              <label className={labelClass}>مقصد کلیک</label>
              <select
                value={form.destination}
                onChange={(e) => updateField("destination", e.target.value)}
                className={inputClass}
              >
                <option value="products">کالاها</option>
                <option value="landing">صفحه فرود</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="is-active"
                type="checkbox"
                checked={Boolean(form.is_active)}
                onChange={(e) => updateField("is_active", e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 accent-[#1E3A5F]"
              />
              <label htmlFor="is-active" className="text-sm font-medium text-gray-700">
                رویداد فعال باشد
              </label>
            </div>

            <div>
              <label className={labelClass}>بنر دسکتاپ</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => updateField("desktop_banner", e.target.files?.[0] || null)}
                className={fileInputClass}
              />
              {errors.desktop_banner && (
                <p className={errorTextClass}>
                  {Array.isArray(errors.desktop_banner)
                    ? errors.desktop_banner.join(" ")
                    : errors.desktop_banner}
                </p>
              )}
              {desktopPreview && (
                <img
                  src={desktopPreview}
                  alt={campaign?.title || "Desktop banner preview"}
                  className="mt-3 h-28 w-full rounded-lg border border-gray-200 object-cover"
                />
              )}
            </div>

            <div>
              <label className={labelClass}>بنر موبایل</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => updateField("mobile_banner", e.target.files?.[0] || null)}
                className={fileInputClass}
              />
              {errors.mobile_banner && (
                <p className={errorTextClass}>
                  {Array.isArray(errors.mobile_banner)
                    ? errors.mobile_banner.join(" ")
                    : errors.mobile_banner}
                </p>
              )}
              {mobilePreview && (
                <img
                  src={mobilePreview}
                  alt={campaign?.title || "Mobile banner preview"}
                  className="mt-3 h-28 w-full rounded-lg border border-gray-200 object-cover"
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 border-t border-gray-100 px-4 py-4 sm:px-5">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`${btnBase} ${btnPrimary}`}
          >
            {isSubmitting ? "در حال ذخیره..." : "ذخیره"}
          </button>

          <button
            type="button"
            onClick={onCancel}
            className={`${btnBase} ${btnSecondary}`}
          >
            انصراف
          </button>
        </div>
      </div>
    </form>
  );
};

export default CampaignForm;
